// WebSocket connection manager for real-time market data
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3001';

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.subscriptions = new Set();
    this.messageHandlers = new Map();
    this.isConnected = false;
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Resubscribe to channels
          this.subscriptions.forEach(channel => {
            this.send({ type: 'subscribe', channel });
          });
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('WebSocket disconnected');
          this.isConnected = false;
          this.attemptReconnect();
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect().catch(err => {
          console.error('Reconnection failed:', err);
        });
      }, delay);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  subscribe(channel, handler) {
    this.subscriptions.add(channel);
    
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, []);
    }
    this.messageHandlers.get(channel).push(handler);

    if (this.isConnected) {
      this.send({ type: 'subscribe', channel });
    }
  }

  unsubscribe(channel, handler) {
    this.subscriptions.delete(channel);
    
    if (this.messageHandlers.has(channel)) {
      const handlers = this.messageHandlers.get(channel);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
      
      if (handlers.length === 0) {
        this.messageHandlers.delete(channel);
        if (this.isConnected) {
          this.send({ type: 'unsubscribe', channel });
        }
      }
    }
  }

  handleMessage(message) {
    const { type, channel, data } = message;
    
    if (type === 'pong') {
      return; // Heartbeat response
    }
    
    if (this.messageHandlers.has(channel)) {
      const handlers = this.messageHandlers.get(channel);
      handlers.forEach(handler => {
        try {
          handler(data, message);
        } catch (error) {
          console.error('Message handler error:', error);
        }
      });
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, message not sent:', data);
    }
  }

  close() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }
}

// Singleton instance
const wsManager = new WebSocketManager();

// Auto-connect
wsManager.connect().catch(err => {
  console.error('Initial WebSocket connection failed:', err);
});

// Heartbeat to keep connection alive
setInterval(() => {
  if (wsManager.isConnected) {
    wsManager.send({ type: 'ping' });
  }
}, 30000);

export default wsManager;
